import api from '../../../components/services/apiService';
import {
  PaginationInfo,
  RemediationItemProgress,
  RemediationPlanDraft,
  Vulnerability,
  VulnerabilityBannerResponse,
  VulnerabilityStats,
} from '../Utils/types';
import { buildRemediationPlanName, pluralizeDevices } from './patchifiLogic';

type FetchOsUpdateVulnerabilitiesParams = {
  tenantId: string;
  source: string;
  severity?: string;
  pageNumber: number;
  pageSize: number;
  search?: string;
};

type FetchOsUpdateVulnerabilitiesResult = {
  vulnerabilities: Vulnerability[];
  paginationInfo: PaginationInfo | null;
};

type FetchOsUpdateVulnerabilitiesBannerParams = {
  tenantId: string;
  source: string;
};

const normalizeBannerStats = (data: any): VulnerabilityStats => ({
  totalVulnerabilities: Number(data?.totalVulnerabilities ?? 0),
  totalKEV: Number(data?.totalKEV ?? data?.totalKev ?? 0),
  totalCritical: Number(data?.totalCritical ?? 0),
  totalEPSSAboveThreshold: Number(data?.totalEPSSAboveThreshold ?? data?.totalEpssAboveThreshold ?? 0),
  affectedDevices: Number(data?.affectedDevices ?? 0),
});

const normalizeSourceForQuery = (source: string): string | undefined => {
  const normalized = source?.trim();
  if (!normalized) return undefined;
  if (normalized.toLowerCase() === 'patchifi') return undefined;
  return normalized.toUpperCase().replace(/\s+/g, '');
};

const getAgeInDays = (publishedDateValue?: string) => {
  if (!publishedDateValue) return 0;
  const publishedDate = new Date(publishedDateValue);
  if (Number.isNaN(publishedDate.getTime())) return 0;
  const today = new Date();
  return Math.floor((today.getTime() - publishedDate.getTime()) / (1000 * 60 * 60 * 24));
};

const toDisplaySeverity = (severity?: string) => {
  if (!severity) return 'Unknown';
  const normalized = severity.toLowerCase();
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const toNumber = (value: unknown, fallback = 0): number => {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const buildRowKey = (item: any, pageNumber: number, index: number) => {
  const compositeKey = [
    'OS',
    item.softwareAssetInventoryId,
    item.cveId || item.cveID,
    item.packageIdentifier,
    item.kbId || item.KBId,
    item.version,
    item.title || item.name,
    item.cveUrl,
  ]
    .filter(Boolean)
    .join('::');

  return compositeKey || `OS::page-${pageNumber}::row-${index}`;
};

const mapOsItemToVulnerability = (item: any, pageNumber: number, index: number): Vulnerability => {
  const kbId = item.kbId || item.KBId || item.kbID || '';
  const packageIdentifier = item.packageIdentifier || kbId || item.cveId || item.cveID || `os-${index}`;

  return {
    rowKey: buildRowKey(item, pageNumber, index),
    softwareAssetInventoryId: String(item.softwareAssetInventoryId || `${packageIdentifier}-${index}`),
    cveId: item.cveId || item.cveID || 'N/A',
    cveUrl: item.cveUrl,
    title: item.title || item.name || kbId || 'OS Update Vulnerability',
    severity: toDisplaySeverity(item.severity || item.baseSeverity),
    cvssScore: toNumber(item.cvssScore ?? item.cvss),
    epssScore: toNumber(item.epssScore),
    kev: item.isKEV ?? item.kev ?? item.isKev ?? item.knownExploited ?? null,
    affectedDevicesCount: toNumber(
      item.affectedDevicesCount ?? item.totalAffectedDevices ?? item.totalEndpointsAffected,
      toNumber(item.installedEndpoints) + toNumber(item.installedServers),
    ),
    ageInDays: typeof item.ageInDays === 'number' ? item.ageInDays : getAgeInDays(item.cveDatePublished || item.publishedDate),
    description: item.description || item.descriptions,
    version: item.version || item.currentVersion,
    packageIdentifier,
    includedAgentIds: Array.isArray(item.includedAgentIds)
      ? item.includedAgentIds.filter((agentId: unknown): agentId is string => typeof agentId === 'string' && !!agentId)
      : [],
    updateType: 'OS',
    kbId,
    fixVersion: item.fixVersion || item.category || item.latestVersion || '',
  };
};

export const fetchOsUpdateVulnerabilities = async (
  params: FetchOsUpdateVulnerabilitiesParams,
): Promise<FetchOsUpdateVulnerabilitiesResult> => {
  const queryParams = new URLSearchParams({
    pageNumber: String(params.pageNumber),
    pageSize: String(params.pageSize),
  });

  const sourceParam = normalizeSourceForQuery(params.source);
  if (sourceParam) queryParams.set('source', sourceParam);

  if (params.severity?.trim()) {
    queryParams.set('severity', params.severity.trim().toUpperCase());
  }

  if (params.search?.trim()) {
    queryParams.set('search', params.search.trim());
  }

  const response = await api.get(`vulnerabilityservice/os-update/${params.tenantId}/vulnerabilities?${queryParams.toString()}`);
  const payload = response?.data ?? {};
  const data = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];

  const mappedData = data.map((item: any, index: number) => mapOsItemToVulnerability(item, params.pageNumber, index));
  const pagination = payload?.pagination;

  const paginationInfo: PaginationInfo = {
    pageNumber: pagination?.pageNumber ?? params.pageNumber,
    pageSize: pagination?.pageSize ?? params.pageSize,
    totalRecords: pagination?.totalRecords ?? mappedData.length,
    totalPages: pagination?.totalPages ?? Math.max(1, Math.ceil(mappedData.length / params.pageSize)),
    hasPreviousPage: pagination?.hasPreviousPage ?? params.pageNumber > 1,
    hasNextPage:
      pagination?.hasNextPage ??
      (pagination?.totalPages ? params.pageNumber < pagination.totalPages : mappedData.length >= params.pageSize),
  };

  return {
    vulnerabilities: mappedData,
    paginationInfo,
  };
};

export const fetchOsUpdateVulnerabilitiesBanner = async (
  params: FetchOsUpdateVulnerabilitiesBannerParams,
): Promise<VulnerabilityBannerResponse> => {
  const queryParams = new URLSearchParams();
  const sourceParam = normalizeSourceForQuery(params.source);

  // Patchifi must not send `source` in query for OS banner endpoint.
  if (sourceParam) {
    queryParams.set('source', sourceParam);
  }

  const queryString = queryParams.toString();
  const endpoint = queryString
    ? `vulnerabilityservice/os-update/${params.tenantId}/vulnerabilities/banner?${queryString}`
    : `vulnerabilityservice/os-update/${params.tenantId}/vulnerabilities/banner`;

  const response = await api.get(endpoint);
  const payload = response?.data ?? {};
  const statsSource = payload?.data ?? payload;

  return {
    data: normalizeBannerStats(statsSource),
    message: payload?.message ?? '',
    isSuccess: payload?.isSuccess ?? true,
    errorCode: payload?.errorCode ?? null,
  };
};

const OS_AGENTS_PAGE_SIZE = 100;
const KB_ID_PATTERN = /^KB\d+$/i; // createRemediationPlan treats anything else as a software package

// All devices that need a KB. An agent with no name no longer resolves to a device, so it is left
// out, the same as the KB's device page.
const getOsUpdateDeviceIds = async (
  tenantId: string,
  kbId: string,
  getOsUpdateAgents: (tenantId: string, kbId: string, pageNumber: number, pageSize: number) => Promise<any>,
) => {
  const deviceIds = new Set<string>();
  let pageNumber = 1;
  let hasNextPage = true;

  while (hasNextPage) {
    const response = await getOsUpdateAgents(tenantId, kbId, pageNumber, OS_AGENTS_PAGE_SIZE);
    if (!response?.isSuccess) throw new Error(`Could not load devices for ${kbId}`);

    const agents: any[] = response.data ?? [];
    agents.filter((agent) => agent.agentId && agent.agentName).forEach((agent) => deviceIds.add(agent.agentId));

    hasNextPage = !!response.pagination?.hasNextPage && agents.length > 0;
    pageNumber += 1;
  }

  return Array.from(deviceIds);
};

// The OS counterpart of prepareRemediationPlans. The KB already says what to install, so there is
// no version to look up and the software inventory isn't involved: all a plan needs is which devices
// need the KB. Several CVEs can share one KB, so rows are merged into one plan per KB.
export async function prepareOsRemediationPlans(params: {
  vulnerabilities: Vulnerability[];
  selectedTenantId: string;
  getOsUpdateAgents: (tenantId: string, kbId: string, pageNumber: number, pageSize: number) => Promise<any>;
  onProgress?: (update: RemediationItemProgress) => void;
  shouldStop?: () => boolean;
}): Promise<RemediationPlanDraft[]> {
  const drafts = new Map<string, RemediationPlanDraft>();

  for (const vulnerability of params.vulnerabilities) {
    if (params.shouldStop?.()) break;

    const key = vulnerability.rowKey;
    const title = vulnerability.title || vulnerability.kbId || vulnerability.cveId || 'Unknown vulnerability';
    const cveId = vulnerability.cveId;
    const kbId = vulnerability.kbId?.trim().toUpperCase();

    if (!kbId || !KB_ID_PATTERN.test(kbId)) {
      params.onProgress?.({ key, title, cveId, stage: 'skipped', message: 'No KB ID for this update' });
      continue;
    }

    try {
      let draft = drafts.get(kbId);

      if (!draft) {
        params.onProgress?.({ key, title, cveId, stage: 'processing', message: 'Finding devices…' });
        const deviceIds = await getOsUpdateDeviceIds(params.selectedTenantId, kbId, params.getOsUpdateAgents);

        if (deviceIds.length === 0) {
          params.onProgress?.({ key, title, cveId, stage: 'skipped', message: 'No affected devices found' });
          continue;
        }

        draft = {
          key: kbId,
          softwareName: title,
          packageIdentifier: kbId,
          kbId,
          currentVersions: [],
          targetVersion: '',
          deviceIds,
          cveIds: [],
          planName: buildRemediationPlanName(kbId),
        };
        drafts.set(kbId, draft);
      }

      if (cveId && cveId !== 'N/A') draft.cveIds = Array.from(new Set([...draft.cveIds, cveId]));

      params.onProgress?.({ key, title, cveId, stage: 'success', message: `${pluralizeDevices(draft.deviceIds.length)} to update` });
    } catch (error) {
      console.error(`Error preparing OS remediation for ${kbId}:`, error);
      params.onProgress?.({ key, title, cveId, stage: 'failed', message: 'Could not find affected devices' });
    }
  }

  return Array.from(drafts.values());
}
